import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Eye, FilePlus2, LayoutGrid, List, Phone, Search } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { Spinner, STATUSES, STAGES, StatusBadge, fmtDay, inputCls } from "@/components/portal/shared";
import { BookVisitModal } from "@/components/portal/QuickActions";

export default function LeadsPage() {
  const { api } = usePortalAuth();
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("newest");
  const [mode, setMode] = useState("list");
  const [visitLead, setVisitLead] = useState(null);

  const load = (s = status, query = q) =>
    api.get("/leads", { params: { status: s, q: query } }).then((r) => setLeads(r.data.leads)).catch(() => setLeads([]));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => {
    const rows = [...(leads || [])];
    if (sort === "oldest") rows.sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
    if (sort === "name") rows.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
    return rows;
  }, [leads, sort]);

  if (leads === null) return <Spinner />;

  return (
    <div data-testid="leads-page" className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="leads-search"
            value={q}
            onChange={(e) => { setQ(e.target.value); load(status, e.target.value); }}
            placeholder="Search name, phone, postcode, ref…"
            className={`${inputCls} pl-10 rounded-full`}
          />
        </div>
        <select
          data-testid="leads-status-filter"
          value={status}
          onChange={(e) => { setStatus(e.target.value); load(e.target.value, q); }}
          className={`${inputCls} w-auto`}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="leads-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={`${inputCls} w-auto`} aria-label="Sort leads">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name A–Z</option>
        </select>
        <div className="flex rounded-full border border-warm-300 bg-white p-1" role="tablist" aria-label="View mode">
          {[[ "list", List, "List" ], [ "board", LayoutGrid, "Board" ]].map(([m, Icon, label]) => (
            <button
              key={m}
              type="button"
              data-testid={`leads-mode-${m}`}
              onClick={() => setMode(m)}
              aria-pressed={mode === m}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${mode === m ? "bg-navy-900 text-white" : "text-slate-500 hover:text-navy-900"}`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      {sorted.length === 0 ? (
        <p className="rounded-2xl border border-warm-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="leads-empty">No enquiries match.</p>
      ) : mode === "list" ? (
        <div className="rounded-2xl border border-warm-200 bg-white shadow-sm overflow-hidden">
          <ul className="divide-y divide-warm-200">
            {sorted.map((l) => (
              <li key={l.id} className="px-5 py-4 hover:bg-warm-100/50 transition-colors" data-testid={`lead-row-${l.id}`}>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
                  <span className="min-w-44">
                    <span className="block text-sm font-semibold text-slate-900">
                      {l.firstName} {l.lastName}
                      {l.repeat && <span className="ml-2 rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Repeat</span>}
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5">{l.interest || "General enquiry"} · {l.cust_ref}</span>
                  </span>
                  <span className="text-xs text-slate-600">{l.postcode || "—"}</span>
                  <span className="text-xs text-slate-600 hidden md:block">{l.phone || "—"}</span>
                  <span className="text-xs text-slate-500 hidden lg:block max-w-36 truncate">{l.source}</span>
                  <span className="text-xs text-slate-500 hidden lg:block">{l.assigned_to || "Unassigned"}</span>
                  <span className="text-xs text-slate-400">{fmtDay(l.created_at)}</span>
                  <span className="ml-auto"><StatusBadge s={l.status} /></span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  <Link to={`/portal/customers/${l.id}`} data-testid={`lead-view-${l.id}`} className="inline-flex items-center gap-1 rounded-full border border-warm-300 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-navy-800/40">
                    <Eye className="h-3 w-3" /> View
                  </Link>
                  {l.phone && (
                    <a href={`tel:${l.phone}`} data-testid={`lead-call-${l.id}`} className="inline-flex items-center gap-1 rounded-full border border-warm-300 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-navy-800/40">
                      <Phone className="h-3 w-3" /> Call
                    </a>
                  )}
                  <button type="button" data-testid={`lead-book-visit-${l.id}`} onClick={() => setVisitLead(l)} className="inline-flex items-center gap-1 rounded-full border border-warm-300 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-navy-800/40">
                    <CalendarPlus className="h-3 w-3" /> Book Visit
                  </button>
                  <Link to={`/portal/customers/${l.id}?tab=quotes`} data-testid={`lead-create-quote-${l.id}`} className="inline-flex items-center gap-1 rounded-full border border-warm-300 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-navy-800/40">
                    <FilePlus2 className="h-3 w-3" /> Create Quote
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1" data-testid="leads-board">
          {STAGES.map(([stage, statuses]) => {
            const cards = sorted.filter((l) => statuses.includes(l.status || "New Lead"));
            return (
              <div key={stage} className="w-60 shrink-0" data-testid={`board-col-${stage.toLowerCase()}`}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2.5 px-1">
                  {stage} <span className="text-slate-400 font-normal">({cards.length})</span>
                </h3>
                <div className="space-y-2.5">
                  {cards.map((l) => (
                    <Link
                      key={l.id}
                      to={`/portal/customers/${l.id}`}
                      data-testid={`board-card-${l.id}`}
                      className="block rounded-xl border border-warm-200 bg-white p-3.5 shadow-sm hover:shadow-md hover:border-navy-800/30 transition-all"
                    >
                      <p className="text-sm font-semibold text-slate-900">{l.firstName} {l.lastName}</p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{l.interest || "General"}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{l.postcode || "—"}</p>
                      <div className="mt-2"><StatusBadge s={l.status} /></div>
                    </Link>
                  ))}
                  {cards.length === 0 && <p className="rounded-xl border border-dashed border-warm-300 p-4 text-center text-[11px] text-slate-400">Empty</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {visitLead && <BookVisitModal lead={visitLead} onClose={() => setVisitLead(null)} onDone={() => load()} />}
    </div>
  );
}
